import { useMemo, useState, useEffect, CSSProperties } from 'react'
import { motion } from 'framer-motion'
import Markdown, { type Options } from 'react-markdown'
import remarkGfm from 'remark-gfm'

import ChatBubbleStem from './chat-bubble-stem'
import FeedbackButtons from './feedback-buttons'
import SourceLink from './source-link'
import Code from '@/components/ui/code/code'
import CopyButton from '@/components/copy-button'
import Disclosure from '@/components/ui/disclosure'
import LeadCaptureForm from './lead-capture-form'

import ThumbsUpIcon from '@/icons/bwi/thumbs-up.svg'

import { useSearch } from '../search-provider'
import { cx } from '@/utils/strings'
import { useTranslations } from '@/utils/locale'
import { shouldFilterDocumentLink } from './utils/document'
import type { ChatMessage, AlgoliaSearchTool } from './types'

import 'property-information'

export type ChatBubbleVariant = 'bubble' | 'text-black' | 'text-white'

type ChatBubbleProps = ChatMessage & {
  state: 'streaming' | 'complete' | 'error'
  isSelected?: boolean
  animated?: boolean
  size?: 'sm' | 'md'
  /** 'bubble' is the default pill style; 'text-black'/'text-white' render just the content with no background or stem, in the given text color. */
  variant?: ChatBubbleVariant
  onViewSources?: () => void
  onFeedback?: (feedback: 'up' | 'down') => void
  onStreamUpdate?: () => void
  /** Defaults to the global `setAiQuery` trigger; override for chat instances that manage their own conversation (e.g. a hero-embedded widget). */
  onSuggestionClick?: (suggestion: string) => void
  /** Defaults to the global `setAiQuery` trigger; override for chat instances that manage their own conversation. */
  onLeadFormSubmit?: () => void
}

export default function ChatBubble({
  role,
  parts,
  state,
  animated = true,
  isSelected = false,
  size = 'md',
  variant = 'bubble',
  onFeedback,
  onStreamUpdate,
  onSuggestionClick,
  onLeadFormSubmit,
}: ChatBubbleProps) {
  const { t } = useTranslations()
  const [feedbackSent, setFeedbackSent] = useState<'up' | 'down' | null>(null)
  const { setAiQuery, askAiAgent } = useSearch()
  const handleSuggestionClick = onSuggestionClick ?? setAiQuery
  const handleLeadFormSubmit = onLeadFormSubmit ?? (() => setAiQuery('Form submitted'))

  const message = useMemo(() => {
    const content = parts?.reduce((acc, next, index) => {
      if (next.type === 'text') {
        acc += `${next.text}`
      } else if (next.type.startsWith('tool-algolia_search_index')) {
        const { input, output } = next as unknown as AlgoliaSearchTool
        if ((output?.hits?.length ?? 0) > 0 && input?.query) {
          acc += `Found search results for: \`${input.query}\`\n`
          output?.hits?.forEach((hit) => {
            acc += `- [${hit.title}](${hit.url})\n`
          })
        }
      }
      if (index + 1 !== parts.length) {
        acc += '\n\n'
      }
      return acc
    }, '')
    if (!content) return ''
    return content
  }, [parts])

  const [displayedTextPartIndices, setDisplayedTextPartIndices] = useState<Record<number, number>>({})

  const isUser = role === 'user'

  // Memoized Markdown components configuration to avoid duplication
  const markdownComponents = useMemo(
    () =>
      ({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        h1: ({ node, content, ...props }) => (
          <h1
            {...props}
            className={cx(
              'header-gradient-reset py-0 text-2xl font-semibold',
              size === 'sm' ? 'text-xl mb-2 mt-3' : 'text-xl md:text-2xl mb-3 mt-5'
            )}
          />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        h2: ({ node, content, ...props }) => (
          <h2
            {...props}
            className={cx(
              'header-gradient-reset py-0 font-semibold',
              size === 'sm' ? 'text-lg mb-1.5 mt-3' : 'text-lg md:text-xl mb-2 mt-4'
            )}
          />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        h3: ({ node, content, ...props }) => (
          <h3
            {...props}
            className={cx(
              'header-gradient-reset py-0 font-semibold',
              size === 'sm' ? 'text-base mb-1 mt-2' : 'text-base md:text-lg mb-1 mt-3'
            )}
          />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        h4: ({ node, content, ...props }) => (
          <h4
            {...props}
            className={cx(
              'header-gradient-reset py-0 text-sm md:text-base font-semibold',
              size === 'sm' ? 'mb-1 mt-1.5' : 'mb-2 mt-3'
            )}
          />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        h5: ({ node, content, ...props }) => (
          <h5
            {...props}
            className={cx(
              'header-gradient-reset py-0 text-xs md:text-sm font-semibold',
              size === 'sm' ? 'mb-0.5 mt-1' : 'mb-1 mt-2'
            )}
          />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        h6: ({ node, content, ...props }) => (
          <h6
            {...props}
            className={cx(
              'header-gradient-reset py-0 text-xs md:text-sm font-semibold',
              size === 'sm' ? 'mb-0.5 mt-0.5' : 'mb-1 mt-1'
            )}
          />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        p: ({ node, content, ...props }) => (
          <p
            {...props}
            className={cx(
              'mb-1 md:mb-2 last:mb-0 leading-tight md:leading-normal overflow-hidden',
              size === 'sm' ? 'text-sm' : 'text-sm md:text-base'
            )}
          />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ol: ({ node, content, ...props }) => (
          <ol {...props} className={cx('pl-8', size === 'sm' ? 'text-sm' : 'text-sm md:text-base')} />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ul: ({ node, content, ...props }) => (
          <ul {...props} className={cx('pl-8', size === 'sm' ? 'text-sm' : 'text-sm md:text-base')} />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        li: ({ node, content, ...props }) => (
          <li {...props} className={cx('mb-1 md:mb-2 last:mb-0', size === 'sm' ? 'text-sm' : 'text-sm md:text-base')} />
        ),
        code: ({ children, className }) => {
          if (!className) {
            return (
              <code className="bg-secondary-200 dark:bg-secondary-800 text-secondary-800 dark:text-white px-1 py-0.5 rounded-xs text-sm">
                {children}
              </code>
            )
          }
          const match = /language-(\w+)/.exec(className || '')
          const langStr = match ? match[1] : 'Plain Text'
          return (
            <div className={cx(size === 'sm' ? '[&_code]:text-sm' : '[&_code]:text-sm md:[&_code]:text-base')}>
              <Code body={children?.toString() || ''} language={langStr as any} />
            </div>
          )
        },
        a: ({ children, href }) => {
          const content = children?.toString() || ''
          if (!content || (href && shouldFilterDocumentLink(href))) {
            return null
          }
          const to = href?.replace(/\.md$/, '/') // Transform markdown links to webpage links
          const citationNumber = content.match(/^\[(\d+)\]$/)?.[1]
          const link = (
            <a
              href={to}
              target="_blank"
              rel="noreferrer noopener"
              className={cx(
                variant === 'bubble' && role === 'user' && 'text-white',
                variant === 'text-white' && 'text-bw-teal-highlight'
              )}
              onClick={() => {
                if (typeof window.plausible !== 'undefined') {
                  window.plausible('AI Chat Link Clicked', {
                    props: { link: to, agent: askAiAgent } as any,
                  })
                }
              }}
            >
              {citationNumber || children}
            </a>
          )
          return link
        },
        table: ({ children }) => (
          <div className="overflow-x-auto max-w-full">
            <table className="text-sm">{children}</table>
          </div>
        ),
        th: ({ children }) => <th className="p-2 text-left">{children}</th>,
        td: ({ children }) => <td className="p-2">{children}</td>,
        tr: ({ children }) => <tr className="not-last:border-b">{children}</tr>,
      }) as Options['components'],
    [size, role, variant]
  )

  // Render individual text parts with streaming
  useEffect(() => {
    if (state === 'streaming' && role === 'assistant') {
      parts?.forEach((part, index) => {
        if (part.type === 'text' && part.text) {
          const currentLength = displayedTextPartIndices[index] || 0
          if (part.text.length > currentLength) {
            setTimeout(() => {
              setDisplayedTextPartIndices((prev) => ({
                ...prev,
                [index]: Math.min(currentLength + 1, part.text.length),
              }))
              onStreamUpdate?.()
            }, 1)
          }
        }
      })
    }
  }, [parts, displayedTextPartIndices, state, role, onStreamUpdate])

  const hasRenderableParts = (parts ?? []).some((part) => {
    if (part.type === 'text') return Boolean(part.text)
    if (part.type === 'data-suggestions') return true
    if (part.type === 'tool-showLeadsForm') return true
    if (part.type.startsWith('tool-algolia_search_index')) return true
    return false
  })
  const noResponse = role === 'assistant' && state === 'complete' && !message && !hasRenderableParts
  const isErrorState = (!isSelected && role === 'assistant' && state === 'error') || noResponse

  return (
    <motion.div
      className={cx(
        'relative inline-block max-w-full',
        variant === 'bubble' && isUser && size === 'md' && 'pl-1 md:pl-8',
        variant === 'bubble' && isUser && size === 'sm' && 'pl-1 md:pl-2',
        variant === 'bubble' && !isUser && size === 'md' && 'pr-1 md:pr-8',
        variant === 'bubble' && !isUser && size === 'sm' && 'pr-1 md:pr-2'
      )}
      transition={{
        type: 'spring',
        damping: 10,
        stiffness: 100,
        staggerChildren: 0.5,
        staggerDirection: -1,
      }}
      initial={
        animated && {
          y: -15,
        }
      }
      animate={
        animated && {
          y: 0,
        }
      }
    >
      {variant === 'bubble' && (
        <motion.div
          className={cx(
            'absolute bottom-0 w-[18px] h-[23px] z-[1] transition-colors',
            isUser && 'text-primary-500 -right-2',
            !isUser && '-left-2',
            !isSelected && !isUser && !isErrorState && 'text-secondary-50 dark:text-secondary-900',
            !isSelected && !isUser && isErrorState && 'text-red-700',
            isSelected && 'text-primary-100 dark:text-deepBlue'
          )}
          initial={{ rotate: 0 }}
          animate={{ rotate: 0 }}
          transition={{ type: 'spring', damping: 10, stiffness: 100 }}
          style={
            !isUser
              ? {
                  transform: 'scaleX(-1)',
                }
              : {}
          }
        >
          <ChatBubbleStem reverse={!isUser} />
        </motion.div>
      )}
      <motion.div
        className={cx(
          'inline-flex flex-col max-w-full transition-colors',
          size === 'md' && 'gap-2',
          size === 'sm' && 'gap-1',
          variant === 'bubble' && size === 'md' && 'px-4 md:px-8 py-4 rounded-[28px]',
          variant === 'bubble' && size === 'sm' && 'px-3 py-2 rounded-xl',
          variant === 'bubble' && isUser && 'bg-primary-500 text-white origin-bottom-right',
          variant === 'bubble' &&
            !isSelected &&
            !isUser &&
            !isErrorState &&
            'bg-secondary-50 dark:bg-secondary-900 text-neutral-800 dark:text-white origin-bottom-left',
          variant === 'bubble' &&
            !isSelected &&
            !isUser &&
            (state === 'complete' || state === 'streaming') &&
            size === 'md' &&
            'py-5',
          variant === 'bubble' && !isSelected && !isUser && isErrorState && 'bg-red-700 text-white origin-bottom-left',
          variant === 'bubble' && isSelected && 'bg-primary-100 text-neutral-800 dark:bg-deepBlue dark:text-white',
          variant === 'text-black' && 'text-black',
          variant === 'text-white' && 'text-white'
        )}
        initial={animated && { scale: 0.6 }}
        animate={animated && { scale: 1 }}
        transition={{ ease: 'easeOut', duration: 0.2 }}
      >
        {/* Render all parts */}
        {parts?.map((part, index) => {
          // Handle text parts
          if (part.type === 'text') {
            const textToDisplay =
              state === 'streaming' && role === 'assistant'
                ? part.text?.slice(0, displayedTextPartIndices[index] || 0) || ''
                : part.text || ''

            return (
              <Markdown key={`text-${index}`} components={markdownComponents} remarkPlugins={[remarkGfm]}>
                {textToDisplay}
              </Markdown>
            )
          }

          // Handle tool call parts (e.g., tool-algolia_search_index)
          if (part.type.startsWith('tool-algolia_search_index')) {
            const searchPart = part as unknown as AlgoliaSearchTool
            const inputQuery = searchPart.input?.query || searchPart.input?.originalQuery
            return (
              <div
                key={searchPart?.toolCallId || `search-${index}`}
                className={cx('@container rounded-sm mb-1 overflow-hidden w-screen max-w-full')}
              >
                {searchPart.state === 'input-streaming' && <span className="text-sm italic">{t('Searching...')}</span>}
                {searchPart.state === 'output-available' &&
                  ((searchPart.output?.hits?.length ?? 0) > 0 && inputQuery ? (
                    <Disclosure
                      heading={
                        <div className={'flex flex-col flex-1 text-xs'}>
                          <span
                            className={cx(
                              'text-secondary-500 dark:text-secondary-300 font-medium',
                              variant === 'text-white' && 'text-white'
                            )}
                          >
                            {t('Found search results for')}
                          </span>
                          <span>{inputQuery}</span>
                        </div>
                      }
                      className="rounded-xs px-1! hover:bg-secondary-900/10! dark:hover:bg-white/10!"
                    >
                      <ul className="max-h-[300px] overflow-auto px-2 pb-2 mt-1 list-none scrollbar-thumb-gray-200 scrollbar-thin @xs:flex @xs:gap-1">
                        {searchPart.output?.hits?.map((hit) => (
                          <li key={hit.objectID} className="mb-1 last:mb-0 @xs:shrink-0 max-w-50">
                            <SourceLink
                              hit={hit}
                              size={size === 'sm' ? 'small' : 'medium'}
                              className={cx(variant === 'text-white' && 'text-white')}
                            />
                          </li>
                        ))}
                      </ul>
                    </Disclosure>
                  ) : (
                    inputQuery && (
                      <div className="px-3 flex flex-col text-sm">
                        <span className="text-secondary-500 dark:text-secondary-300">{`${t('No results found for')}`}</span>
                        <span>{inputQuery}</span>
                      </div>
                    )
                  ))}
                {searchPart.state === 'output-denied' && (
                  <div className="p-4">
                    <div className="mb-2">{t('Search denied')}</div>
                  </div>
                )}
                {searchPart.state === 'output-error' && (
                  <div className="p-4">
                    <div className="">{t('Search error')}</div>
                  </div>
                )}
              </div>
            )
          }

          if (part.type === 'step-start') return null

          if (part.type === 'data-suggestions') {
            const { suggestions } = part.data
            return (
              <div key={`suggestions-${index}`} className="mt-1 mb-0.5">
                <ul className="flex flex-wrap gap-1.5 list-none">
                  {suggestions.map((suggestion, idx) => (
                    <li key={idx}>
                      <button
                        className={cx(
                          'px-4 py-2 text-xs font-medium text-left text-primaryBlue border border-gray-300 dark:border-gray-600 rounded-full',
                          'transition-colors duration-200 hover:text-black border-black',
                          variant === 'text-white' && 'text-white hover:bg-white'
                        )}
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )
          }

          if (part.type === 'tool-showLeadsForm') {
            return (
              <LeadCaptureForm
                key={`lead-form-${index}`}
                variant={variant === 'bubble' ? 'container' : 'contents'}
                note={part.input?.notes}
                onSubmit={handleLeadFormSubmit}
              />
            )
          }

          if (import.meta.env.DEV) {
            console.warn('Unknown part type:', part)
          }
          return null
        })}

        {/* Fallback to original message display if no parts */}
        {(!parts || parts.length === 0) && message && (
          <Markdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
            {message}
          </Markdown>
        )}

        {/* Display an error if there are no parts and no message */}
        {noResponse && (
          <div className="text-sm">
            <div>{t('An unexpected issue occurred. Please try again later.')}</div>
          </div>
        )}

        {state === 'complete' && role === 'assistant' && !noResponse && (
          <div className="flex items-center justify-between">
            {onFeedback && feedbackSent === null && (
              <div className="self-end">
                <FeedbackButtons
                  color={variant === 'text-white' ? 'white' : 'black'}
                  onFeedback={(feedback) => {
                    if (!feedbackSent) {
                      onFeedback?.(feedback)
                      setFeedbackSent(feedback)
                    }
                  }}
                />
              </div>
            )}
            {feedbackSent && (
              <div
                className={cx(
                  'flex items-center gap-2 self-end text-sm',
                  feedbackSent === 'up' ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-400'
                )}
              >
                <ThumbsUpIcon
                  className={cx('inline-block w-4 h-4 mr-1 shrink-0', feedbackSent === 'down' && 'rotate-180')}
                />
                {feedbackSent === 'up'
                  ? t('Thank you for your feedback!')
                  : t('We are sorry to hear that. We will use your feedback to improve our assistant.')}
              </div>
            )}
            <CopyButton text={message} className="text-sm" />
          </div>
        )}
        {state === 'streaming' && (
          <div className="flex items-center justify-center w-max animate-pulse">
            <span
              className={cx(
                'animate-text-shimmer [animation-iteration-count:infinite]',
                size === 'md' && 'text-base',
                size === 'sm' && 'text-xs'
              )}
              style={{ '--color-text-shimmer': 'var(--color-secondary-300)' } as CSSProperties}
            >
              {message || (parts?.length ?? 0) > 0 ? t('Typing') : t('Thinking')}...
            </span>
          </div>
        )}
      </motion.div>
    </motion.div>
  )
}
